/**
 * ClientPortalShell — spec-10 Wave 4.3
 *
 * Simpler layout for client-facing portal:
 * - No internal sidebar or module nav
 * - Top navigation bar with home links
 * - AR-primary, EN toggle (Q-CP1)
 * - Mobile-first responsive
 * - Bottom nav bar on mobile
 */

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import BalanceIcon from '@mui/icons-material/Balance';
import SecurityIcon from '@mui/icons-material/Security';
import { isRtl } from '../../../i18n/config';
import { useUiLanguageStore } from '../../../store/uiLanguageStore';
import { useAuth } from '../../../auth/AuthProvider';

// ─── Nav items ────────────────────────────────────────────────────────────────

interface NavItem {
  path: string;
  labelKey: string;
  Icon: React.ElementType;
}

// FIX-4 (fix-iter-1): Profile added to drawer nav as required by Scenario 7.
const NAV_ITEMS: NavItem[] = [
  { path: '/client', labelKey: 'nav.home', Icon: HomeIcon },
  { path: '/client/retainer', labelKey: 'nav.retainer', Icon: BalanceIcon },
  { path: '/client/matters', labelKey: 'nav.matters', Icon: GavelIcon },
  { path: '/client/documents', labelKey: 'nav.documents', Icon: FolderIcon },
  { path: '/client/invoices', labelKey: 'nav.invoices', Icon: ReceiptIcon },
  { path: '/client/request', labelKey: 'nav.request', Icon: AddCircleOutlineIcon },
  { path: '/client/profile', labelKey: 'nav.profile', Icon: PersonIcon },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ClientPortalShell: React.FC = () => {
  const { t, i18n } = useTranslation('client_portal');
  const rtl = isRtl(i18n.language);
  const dir = rtl ? 'rtl' : 'ltr';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toggle, locale } = useUiLanguageStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/client/login');
  };

  const isActive = (path: string) => {
    if (path === '/client') return location.pathname === '/client';
    return location.pathname.startsWith(path);
  };

  // FIX-4 (fix-iter-1): nav list wrapped in <nav aria-label="primary"> for
  // landmark compliance (QA: zero <nav> landmarks at 375×667).
  const navListContent = (
    <Box component="nav" aria-label="primary">
      <List sx={{ p: 1 }}>
        {NAV_ITEMS.map(({ path, labelKey, Icon }) => (
          <ListItemButton
            key={path}
            selected={isActive(path)}
            onClick={() => { navigate(path); setDrawerOpen(false); }}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': { bgcolor: 'rgba(11,37,69,0.08)', color: 'primary.main' },
              '&.Mui-selected:hover': { bgcolor: 'rgba(11,37,69,0.12)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: isActive(path) ? 'primary.main' : 'text.secondary' }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t(labelKey)}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive(path) ? 600 : 400 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }} dir={dir}>
      {/* ── Top AppBar ─────────────────────────────────────────────────── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: '2px solid', borderColor: 'secondary.main', zIndex: theme.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ minHeight: '56px !important', px: { xs: 1, md: 3 }, overflow: 'hidden' }}>
          {isMobile && (
            // FIX-4 (fix-iter-1): aria-label required for a11y landmark compliance.
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: 'text.primary', mr: 1 }}
              aria-label={rtl ? 'فتح القائمة' : 'open navigation'}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo — flexShrink: 1 + minWidth: 0 allows shrinking on narrow viewports */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', flexShrink: 1, minWidth: 0 }}
            onClick={() => navigate('/client')}
          >
            <Box sx={{
              width: 32, height: 32, borderRadius: 1, bgcolor: 'transparent',
              border: '1px solid', borderColor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ color: 'secondary.main', fontWeight: 700, fontSize: '0.85rem', fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif', letterSpacing: '0.5px' }}>
                JY
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'primary.main', lineHeight: 1.1, fontFamily: '"Source Serif 4", "Source Serif Pro", Georgia, serif', letterSpacing: '0.3px', fontSize: '1rem' }}>
                JY Law
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                {rtl ? 'بوابة العملاء' : 'Client Portal'}
              </Typography>
            </Box>
          </Box>

          {/* Desktop nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, mx: 3 }}>
              {NAV_ITEMS.map(({ path, labelKey, Icon }) => (
                <Button
                  key={path}
                  startIcon={<Icon fontSize="small" />}
                  onClick={() => navigate(path)}
                  size="small"
                  sx={{
                    color: isActive(path) ? 'secondary.main' : 'text.secondary',
                    fontWeight: isActive(path) ? 600 : 400,
                    borderRadius: 1,
                    px: 1.5,
                    textTransform: 'none',
                    bgcolor: isActive(path) ? 'rgba(164, 113, 72, 0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(164, 113, 72, 0.08)', color: 'secondary.main' },
                    fontSize: '0.8125rem',
                  }}
                >
                  {t(labelKey)}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Language toggle */}
          <Button
            size="small"
            variant="outlined"
            onClick={toggle}
            aria-label={locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            sx={{ color: 'primary.main', borderColor: 'secondary.main', textTransform: 'none', mr: 1, fontSize: '0.8rem', minWidth: 44, fontWeight: 600, '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(11, 37, 69, 0.04)' } }}
          >
            {locale === 'ar' ? 'EN' : 'ع'}
          </Button>

          {/* User avatar + menu */}
          <Tooltip title={user?.username ?? ''}>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(11, 37, 69, 0.04)', color: 'primary.main', fontSize: '0.85rem', fontWeight: 600, border: '1px solid', borderColor: 'secondary.main' }}>
                {user?.username?.charAt(0).toUpperCase() ?? 'C'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/client/profile'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              {t('nav.profile')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Mobile drawer ──────────────────────────────────────────────── */}
      <Drawer
        anchor={rtl ? 'right' : 'left'}
        open={drawerOpen && isMobile}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 260, bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider', mt: '56px' } }}
      >
        {navListContent}
      </Drawer>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          mt: '56px',
          mb: isMobile ? '56px' : 0,
          flexGrow: 1,
          minHeight: 'calc(100vh - 56px)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
          <Outlet />
        </Container>
      </Box>

      {/* ── Footer privacy link — spec-13 W13.2 / spec-20 J3 ────────────── */}
      {!isMobile && (
        <Box
          component="footer"
          sx={{
            py: 1.5,
            px: 3,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <Button
            size="small"
            startIcon={<SecurityIcon fontSize="small" />}
            onClick={() => navigate('/client/privacy-notice')}
            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.75rem' }}
          >
            {rtl ? 'إشعار الخصوصية' : 'Privacy Notice'}
          </Button>
          <Button
            size="small"
            onClick={() => navigate('/client/privacy-request')}
            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.75rem' }}
          >
            {rtl ? 'طلب بيانات شخصية' : 'Data Subject Request'}
          </Button>
        </Box>
      )}

      {/* ── Mobile bottom nav ──────────────────────────────────────────── */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-around',
            py: 0.75,
          }}
        >
          {NAV_ITEMS.slice(0, 5).map(({ path, labelKey, Icon }) => {
            const active = isActive(path);
            return (
              <Box
                key={path}
                onClick={() => navigate(path)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  cursor: 'pointer', flex: 1, py: 0.5,
                  color: active ? 'primary.main' : 'text.secondary',
                }}
              >
                <Icon fontSize="small" />
                <Typography sx={{ fontSize: '0.625rem', mt: 0.25, fontWeight: active ? 600 : 400 }}>
                  {t(labelKey)}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default ClientPortalShell;
